/* Full-screen countdown overlay shown after the shake trigger fires. Uses
   useFallWatch to detect stillness — if the phone doesn't move for the
   whole countdown, we auto-fire the WhatsApp ICE alert. Any real movement
   or a tap on "I'M OKAY" dismisses. */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { WhatsappLogo, X } from "@phosphor-icons/react";
import { useFallWatch } from "./useShakeTrigger";

const HOLD_SEC = 60;

export default function FallDetectOverlay({ open, settings, onDismiss, onFired }) {
  const [remaining, setRemaining] = useState(HOLD_SEC);

  useEffect(() => {
    if (!open) return;
    setRemaining(HOLD_SEC);
    const t = setInterval(() => {
      setRemaining((s) => Math.max(0, s - 1));
    }, 1000);
    return () => clearInterval(t);
  }, [open]);

  const fireIce = () => {
    const contacts = (settings?.emergency_contacts || []).filter((c) => c.phone);
    const legacy = settings?.emergency_whatsapp;
    const list = contacts.length > 0
      ? contacts
      : (legacy ? [{ name: settings?.emergency_contact_name || "", phone: legacy }] : []);
    if (list.length === 0) {
      toast.error("No emergency contacts configured — set at least one in Settings");
      onDismiss?.();
      return;
    }
    const shoot = (coords) => {
      const base = settings.default_distress_message || "MEDICAL EMERGENCY";
      const suffix = "\n\n[AUTO-FIRED: fall detected — phone motionless for 60s after seizure trigger]";
      const geo = coords
        ? `\nLive location: https://maps.google.com/?q=${coords.latitude},${coords.longitude}\n(±${Math.round(coords.accuracy)}m)`
        : "";
      const msg = encodeURIComponent(base + suffix + geo);
      list.forEach((c, i) => {
        const phone = c.phone.replace(/\D+/g, "");
        setTimeout(() => window.open(`https://wa.me/${phone}?text=${msg}`, "_blank"), i * 350);
      });
      onFired?.();
    };
    try { navigator.vibrate?.([300, 100, 300, 100, 300]); } catch {}
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => shoot(p.coords),
        () => shoot(null),
        { enableHighAccuracy: true, timeout: 4000 },
      );
      setTimeout(() => shoot(null), 4200);
    } else {
      shoot(null);
    }
  };

  useFallWatch({
    enabled: open,
    holdSeconds: HOLD_SEC,
    onConfirm: fireIce,
    onCancel: (reason) => {
      toast.info(reason === "motion-detected" ? "Movement detected — alert cancelled" : "Cancelled");
      onDismiss?.();
    },
  });

  if (!open) return null;

  const pct = ((HOLD_SEC - remaining) / HOLD_SEC) * 100;

  return (
    <div
      data-testid="nl-fall-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center p-6"
      style={{ background: "rgba(11,19,43,0.96)", backdropFilter: "blur(8px)" }}
    >
      <div className="w-full max-w-sm text-center">
        <div className="font-label text-[10px] tracking-widest mb-2" style={{ color: "var(--nl-danger)" }}>
          / FALL DETECTED
        </div>
        <div className="font-display font-black leading-none mb-2" style={{ color: "var(--nl-text)", fontSize: 34 }}>
          Alerting{" "}
          <span style={{ color: "var(--nl-danger)" }}>
            {(() => {
              const list = (settings?.emergency_contacts || []).filter((c) => c.phone);
              if (list.length === 0) return settings?.emergency_contact_name || "your contact";
              if (list.length === 1) return list[0].name || "your contact";
              return `${list.length} contacts`;
            })()}
          </span>{" "}
          in
        </div>
        <div className="font-display font-black tabular-nums my-4" style={{ color: "var(--nl-danger)", fontSize: 96, lineHeight: 1 }}>
          {remaining}s
        </div>

        <div className="h-2 w-full rounded-full mb-6" style={{ background: "var(--nl-line)" }}>
          <div className="h-2 rounded-full transition-all duration-500"
               style={{ width: `${pct}%`, background: "var(--nl-danger)" }} />
        </div>

        <div className="text-xs mb-6" style={{ color: "var(--nl-muted)" }}>
          A seizure was auto-detected and you haven't moved. WhatsApp with your live GPS is about to send.
        </div>

        <button
          data-testid="nl-fall-cancel"
          onClick={() => { toast.success("Cancelled — hope you're okay"); onDismiss?.(); }}
          className="w-full h-16 rounded-2xl font-label text-sm tracking-widest inline-flex items-center justify-center gap-2 active:scale-95 transition-transform"
          style={{ background: "var(--nl-accent)", color: "#001018" }}
        >
          <X size={18} weight="bold" /> I'M OKAY — CANCEL
        </button>

        <button
          data-testid="nl-fall-send-now"
          onClick={fireIce}
          className="mt-3 w-full h-12 rounded-lg font-label text-xs tracking-widest inline-flex items-center justify-center gap-2 active:scale-95"
          style={{ border: "1px solid var(--nl-danger)", color: "var(--nl-danger)", background: "transparent" }}
        >
          <WhatsappLogo size={14} weight="fill" /> SEND NOW — DON'T WAIT
        </button>

        <div className="mt-4 text-[10px]" style={{ color: "var(--nl-muted)" }}>
          Any real movement of the phone will auto-cancel this alert.
        </div>
      </div>
    </div>
  );
}
